import type { GeoMap } from "@esWebAPI/es/model/GeoMap";
import type { FeatureViewModel } from "../FeatureViewModel";
import type { ILayerView, LayerInfo } from "@esWebAPI/es/model/Layer";
import type { IEventChannel } from "@esWebAPI/es/model/Models";

export default class MetaModule {
    private geoMap: GeoMap;
    private analysis;

    private eventChannel;
    private featureViewModel: FeatureViewModel;

    private id: number;
    private title: string;
    private description: string;
    private workspaceId: number;
    private lastModified: number;
    private baseMapStyleId: number | null;
    private baseMapType: string;
    private includeTable: boolean;
    private analysisId: number | undefined;
    private activeLayerViewId: number | null;
    private owner : boolean;
    private type : string;
    private shareStatus: string | null;
    private parentShareStatus: string | null;
    private parentId: number;
    private parentLastModified:  number;
    private changed: boolean | null;
    private cloned: number;
    private weatherOverlay;
    
    private configurableGeoMap: boolean;
    private configurableParentGeoMap: boolean;
    private editableGeoMap: boolean;
    private configurableWorkspace: boolean;

    constructor(geoMap: GeoMap, isOwnerOfGeoMap: boolean, eventChannel: IEventChannel, featureViewModel: FeatureViewModel, geoMapShareStatus: string,
        isConfigurableGeoMap: boolean, isConfigurableParentGeoMap: boolean, isEditableGeoMap: boolean, isConfigurableWorkspace: boolean,
        analysis: any, parentGeoMapShareStatus: string) {

        this.geoMap = geoMap;
        this.eventChannel = eventChannel;
        this.featureViewModel = featureViewModel;

        this.id = geoMap.id;
        this.lastModified = -1;

        this.setGeoMapMetadata(geoMap, isOwnerOfGeoMap, isConfigurableGeoMap, isConfigurableParentGeoMap, isEditableGeoMap, isConfigurableWorkspace, analysis);
        this.setGeoMapShareStatus(geoMapShareStatus);
        this.setParentGeoMapShareStatus(parentGeoMapShareStatus);

        this.addListeners();
    }

    private addListeners() {
        // This is to catch case when sync sf objects updates the geomap last modified (a knock-on effect of updating the layerView.importMeta)
        this.featureViewModel.eventChannel.addListener("onUpdate", () => {
            es.api.geoMap.get(this.id, {"noCache": true}).done((rs) => {
                this.setLastModified(rs.lastModified);
                this.parentLastModified = (rs["parentLastModified"] ? rs.parentLastModified : null);
            });
        });
    
        es.api.getEventChannel().addListener("onBeforeUpdateGeomap", (geoMap: GeoMap, isSaveViewOnly: boolean) => {
            if (!isSaveViewOnly) {
                this.changed = true;
            }
        });
    
        es.api.getEventChannel().addListener("onUpdateWorkspace", () => {
            // When renaming ScheduleWorkspace we automatically update ScheduleGeoMap and any related ScheduleTempGeoMap. Thus on workspace update we need to reload current geoMap
            es.api.geoMap.get(this.id).done((geoMap) => {
                this.setGeoMapMetadata(geoMap, this.owner, this.configurableGeoMap, this.configurableParentGeoMap, this.editableGeoMap, this.configurableWorkspace, this.analysis);
            });
        });
    
        es.api.getEventChannel().addListener("onUpdateGeoMap", (rs: GeoMap) => {
            if (rs.id === this.id) {
                // parentId change happens during a "save as" operation
                if (rs["parentId"] && rs.parentId != this.parentId) {
                    // will always be true since we are working on temp map
                    this.configurableGeoMap = true;
                    this.configurableParentGeoMap = true;
                    this.editableGeoMap = true;
                }
                this.setGeoMapMetadata(rs, this.owner, this.configurableGeoMap, this.configurableParentGeoMap, this.editableGeoMap, this.configurableWorkspace, this.analysis);
            }
        });
    
        es.api.getEventChannel().addListener("onRemoveLayerView", (layerView: ILayerView, geoMapId: number, geoMapLastModified: number) => {
            this.changed = true;
            this.setLastModified(geoMapLastModified);
        });

        es.api.getEventChannel().addListener("onCreateLayerView", (layerView: ILayerView, jqXHR: JQueryXHR, geoMapLastModified: number) => {
            this.changed = true;
            this.setLastModified(geoMapLastModified);
        });

        es.api.getEventChannel().addListener("onUpdateLayerView", (layerView: ILayerView, layerInfo: LayerInfo, geoMapLastModified: number, isSuppressMetaChange: boolean) => {
            if (isSuppressMetaChange) {
                // skip
            } else {
                this.changed = true;
            }

            this.setLastModified(geoMapLastModified);
        });

        es.api.getEventChannel().addListener("onCreateGeoMapAnnotation", (geoMapAnnotation, geoMapLastModified: number) => {
            this.changed = true;
            this.setLastModified(geoMapLastModified);
        });

        es.api.getEventChannel().addListener("onUpdateGeoMapAnnotation", (geoMapAnnotation, geoMapLastModified: number) => {
            this.changed = true;
            this.setLastModified(geoMapLastModified);
        });

        es.api.getEventChannel().addListener("onRemoveGeoMapAnnotation", (geoMapAnnotationId, geoMapLastModified: number) => {
            this.changed = true;
            if (geoMapLastModified) {
                this.setLastModified(geoMapLastModified);
            }
        });
    
        es.api.getEventChannel().addListener("onUpdateEntitlements", (entityType: string, entityId: number) => {
            if (entityType == 'geomap' && (this.id == entityId || this.parentId == entityId)) {
                es.api.security.getGeoMapShareStatus([this.id, this.parentId]).done((rs) => {
                    this.setGeoMapShareStatus([rs[0]]);
                    this.setParentGeoMapShareStatus([rs[1]]);
                });
            }
        });
        
        this.eventChannel.addListener("onSetBaseMap", (bm) => {
            this.changed = true;
            this.baseMapStyleId = bm.baseMapStyleId;
            this.baseMapType = bm.baseMapType;
        });
    
        this.eventChannel.addListener("onSetWeatherOverlay", (wo) => {
            this.changed = true;
            this.weatherOverlay = wo;
        });
    }

    private setGeoMapMetadata(geoMap: GeoMap, _isOwnerOfGeoMap: boolean, _isConfigurableGeoMap: boolean, _isConfigurableParentGeoMap: boolean,
        _isEditableGeoMap: boolean, _isConfigurableWorkspace: boolean, analysis: any) {
        this.geoMap = geoMap;
        this.analysis = analysis;

        this.title = geoMap.title;
        this.description = (geoMap["description"] === undefined ? "" : geoMap.description);
        this.workspaceId = geoMap.workspaceId;
        this.setLastModified(geoMap.lastModified);
        this.baseMapStyleId = geoMap.baseMapStyleId;
        this.baseMapType = geoMap.baseMapType;
        this.includeTable = geoMap.includeTable;
        this.analysisId = analysis ? analysis.id : null;
        this.activeLayerViewId = (geoMap["activeLayerViewId"] === undefined ? null : geoMap.activeLayerViewId);
        this.type = geoMap.$type;
        this.parentId = (geoMap["parentId"] ? geoMap.parentId : null);
        this.cloned = geoMap.cloned;
        this.parentLastModified = (geoMap["parentLastModified"] ? geoMap.parentLastModified : null);
        //es.log.info("set parent parentLastModified == " + parentLastModified);
        this.configurableGeoMap = _isConfigurableGeoMap;
        this.configurableParentGeoMap = _isConfigurableParentGeoMap;
        this.editableGeoMap = _isEditableGeoMap;
        this.configurableWorkspace = _isConfigurableWorkspace;
        this.owner = _isOwnerOfGeoMap;
        this.weatherOverlay = geoMap.weatherOverlay;
        if (geoMap.$type.endsWith('TempGeoMap')) {
            this.changed = geoMap.changed;
        } else {
            this.changed = null;
        }
    }

    private setLastModified(newLastModified: number) {
        /*
        console.log("setLastModified", lastModified, newLastModified);
        if(newLastModified < lastModified){
            console.warn("newLastModified is older than current lastModified");
        }
        */
        this.lastModified = newLastModified;
    }


    getId() {
        return this.id;
    }

    getParentId() {
        return this.parentId;
    }

    getParentLastModified() {
        return this.parentLastModified;
    }

    getType() {
        return this.type;
    }

    getShareStatus() {
        return this.shareStatus;
    }

    getTitle() {
        return this.title;
    }

    getDescription() {
        return this.description;
    }

    getWorkspaceId() {
        return this.workspaceId;
    }

    getLastModified() {
        return this.lastModified;
    }

    getBaseMapStyleId() {
        return this.baseMapStyleId;
    }

    getBaseMapType() {
        return this.baseMapType;
    }

    isIncludeTable() {
        return this.includeTable;
    }

    getAnalysisId() {
        return this.analysisId;
    }

    getActiveLayerViewId() {
        return this.activeLayerViewId;
    }

    public setActiveLayerViewId(updatedActiveLayerViewId: number) {
        if (this.activeLayerViewId != updatedActiveLayerViewId) { // 123 == "123" -> true, it would be false with ===
            this.activeLayerViewId = updatedActiveLayerViewId;
            this.eventChannel.fireEvent("onSetActiveLayerViewId", updatedActiveLayerViewId);
        }
    }

    isOwner() {
        return this.owner;
    }

    isConfigurableGeoMap() {
        return this.configurableGeoMap;
    }

    isConfigurableParentGeoMap() {
        return this.configurableParentGeoMap;
    }

    isEditableGeoMap() {
        return this.editableGeoMap;
    }

    isConfigurableWorkspace() {
        return this.configurableWorkspace;
    }

    isChanged() {
        return this.changed;
    }

    isCloned() {
        return this.cloned;
    }

    getWeatherOverlay() {
        return this.weatherOverlay;
    }

    isTemp() {
        return this.type === "TempGeoMap" || this.type === "TerritoryTempGeoMap" || this.type ==="ScheduleTempGeoMap";
    }

    isTerritory() {
        return this.type.startsWith('Territory');
    }

    isSchedule() {
        return this.type.startsWith('Schedule');
    }

    // Convenience method shorthand for checking if map is shared
    isShared() {
        return (this.shareStatus != "PRIVATE");
    }

    // Convenience method shorthand for checking if map is shared
    isParentShared() {
        return (this.parentShareStatus != "PRIVATE");
    }

    getParentSharedStatus() {
        return this.parentShareStatus;
    };

    getGeoMap() {
        const rs = {
            "$type": this.type,
            "id": this.id,
            "title": this.title,
            "description": this.description,
            "baseMapType": this.baseMapType,
            "baseMapStyleId": this.baseMapStyleId,
            "workspaceId": this.workspaceId,
            "includeTable": this.includeTable,
            "activeLayerViewId": this.activeLayerViewId,
            "cloned": this.cloned,
            "lastModified": this.lastModified,
            "weatherOverlay": this.weatherOverlay
        };

        if (this.type.endsWith('TempGeoMap')) {
            rs.parentId = this.parentId;
            rs.parentLastModified = this.parentLastModified;
            rs.changed = this.changed;
        }
        
        if (this.type.startsWith('Territory')) {
            rs.territoryMetaId = this.geoMap.territoryMetaId;
        }
        
        if (this.type.startsWith('Schedule')) {
            rs.scheduleId = this.geoMap.scheduleId;
        }

        return rs;
    }

    private setGeoMapShareStatus(geoMapShareStatusInfo: string) {
        if (geoMapShareStatusInfo === null) {
            // we're in public mode
            this.shareStatus = null;
        } else {
            this.shareStatus = geoMapShareStatusInfo[0].shareStatus;
        }
    }

    private setParentGeoMapShareStatus(parentGeoMapShareStatusInfo) {
        if(parentGeoMapShareStatusInfo === null){
            // we're in public mode
            this.parentShareStatus = null;
        } else {
            this.parentShareStatus = parentGeoMapShareStatusInfo[0].shareStatus;
        }
    }
}
